import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { removeSuffix } from 'shared-modules/hooks/symbol';
import { getServiceQuantityUnit } from 'shared-modules/services';
import { getPipsLabelWithParentheses } from 'shared-modules/utils';
import styles from './orderSettingTable.module.scss';
import { NumberCell as Cell, SeqCell } from '../../../../../../../components/ChartSettings/Cell';
import { Table } from '../../../../../../../components';
import BuySellItem from '../../../../../../../components/BuySellItem/BuySellItem';

const OrderSettingTable = ({ autoTradeSettingsData, selectedStrategy, helpers }) => {
  const orderSettingsResizedSingle = {
    key: 'orderSettingsResizedSingleOmakasePlan',
    default: [48, 76, 53, 62, 67, 67, 66, 66, 88, 88],
  };

  const defaultColumnParams = {
    minWidth: 10,
    width: 100,
    maxWidth: 400,
  };

  const { serviceId, instrumentId } = selectedStrategy.strategyDetail;
  const quantityUnit = getServiceQuantityUnit(serviceId);
  const pipsLabelWithParentheses = getPipsLabelWithParentheses(serviceId, instrumentId);

  const columns = useMemo(
    () => [
      {
        Header: '番号',
        accessor: 'number',
        disableSortBy: true,
        Cell: SeqCell,
        className: classNames(styles.th),
      },
      {
        Header: '銘柄',
        accessor: 'instrumentId',
        disableSortBy: true,
        Cell: ({ cell: { value } }) => removeSuffix(value),
        className: classNames(styles.th),
      },
      {
        Header: '売買',
        accessor: 'side',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell: { value } }) => (value ? <BuySellItem type={Number(value)} /> : '-'),
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: `数量(${quantityUnit})`,
        accessor: (row) => helpers.getQuantity(row.quantity, row.strategySets),
        Cell,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: '価格1',
        accessor: 'entryPrice1',
        Cell,
        isNumber: true,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: '価格2',
        accessor: 'entryPrice2',
        isNumber: true,
        Cell,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: `利確幅\n${pipsLabelWithParentheses}`,
        accessor: 'tp',
        isNumber: true,
        Cell,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: `損切幅\n${pipsLabelWithParentheses}`,
        accessor: 'sl',
        isNumber: true,
        Cell,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: `フォロー値\n${pipsLabelWithParentheses}`,
        accessor: 'follow',
        isNumber: true,
        Cell,
        disableSortBy: true,
        className: classNames(styles.th),
      },
      {
        Header: 'カウンター値\n(価格指定)',
        // can't pass in multi accessor values, so use function -> we can customize what to display simply
        accessor: ({ counterPrice, counter }) => (counterPrice || counterPrice === 0 ? `(${counterPrice})` : counter),
        Cell,
        isNumber: true,
        disableSortBy: true,
        className: classNames(styles.th),
      },
    ],
    [helpers, pipsLabelWithParentheses, quantityUnit],
  );

  return (
    <div className={styles.tableContainer}>
      <div className={styles.title}>注文設定</div>
      <div className={styles.table}>
        <Table
          tableData={autoTradeSettingsData}
          columns={columns}
          key={0}
          rowHight={34}
          resized={orderSettingsResizedSingle}
          headerClassName={styles.tableHeader}
          rowClassName={styles.tableRow}
          defaultColumnParams={defaultColumnParams}
        />
      </div>
    </div>
  );
};

OrderSettingTable.propTypes = {
  selectedStrategy: PropTypes.shape({
    strategyDetail: PropTypes.shape({
      serviceId: PropTypes.string.isRequired,
      instrumentId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  autoTradeSettingsData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  helpers: PropTypes.shape({
    getQuantity: PropTypes.func.isRequired,
  }).isRequired,
};

export default memo(OrderSettingTable);
